import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { remoteAttendanceApi, REASON_OPTIONS } from '../api/remoteAttendanceApi';
import { appToast } from '../../../../components/AppToast';

const DEFAULT_FORM = {
  emp_id: '',
  emp_name: '',
  employeeId: '',
  date: new Date().toISOString().slice(0, 10),
  hour: '09',
  minute: '00',
  ampm: 'am',
  reason_id: '4',
  remarks: '',
  gpsLocation: 'Remote Check-in',
  status: 'Pending',
  firstPunch: '',
  lastPunch: '',
};

export function useRemoteAttendanceEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const isEdit = Boolean(id);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reasonOptions, setReasonOptions] = useState(REASON_OPTIONS);

  // Auto-detect browser location if available
  useEffect(() => {
    if (!isEdit && typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = `${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`;
          setForm((prev) => ({ ...prev, gpsLocation: coords }));
        },
        () => {
          // Location access denied or unavailable — keep 'Remote Check-in' default
        },
        { timeout: 5000 }
      );
    }
  }, [isEdit]);

  // Load dynamic reasons
  useEffect(() => {
    remoteAttendanceApi
      .getReasons()
      .then((reasons) => {
        if (Array.isArray(reasons) && reasons.length > 0) {
          const mapped = reasons.map((r) => ({
            value: String(r.ID || r.id),
            label: r.Name || r.name,
            id: r.ID || r.id,
          }));
          setReasonOptions(mapped);
          if (!isEdit && !form.reason_id) {
            // Find 'Work From Home' or default to first
            const wfh = mapped.find((m) => m.label.toLowerCase().includes('remote') || m.label.toLowerCase().includes('home'));
            setForm((prev) => ({ ...prev, reason_id: String(wfh ? wfh.value : mapped[0].value) }));
          }
        }
      })
      .catch(() => {});
  }, [isEdit, form.reason_id]);

  // Load record for edit
  const loadData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const found = await remoteAttendanceApi.getRecordById(id);
      if (found) {
        setForm({
          emp_id: found.emp_id ? String(found.emp_id) : '',
          emp_name: found.employee || found.emp_name || '',
          employeeId: found.employeeId || `EMP-${found.emp_id}`,
          date: found.date || new Date().toISOString().slice(0, 10),
          hour: found.hour || '09',
          minute: found.minute || '00',
          ampm: found.ampm || 'am',
          reason_id: found.reason_id ? String(found.reason_id) : '4',
          remarks: found.remarks || '',
          gpsLocation: found.gpsLocation || 'Remote Check-in',
          status: found.status || 'Pending',
          firstPunch: found.firstPunch || '',
          lastPunch: found.lastPunch || '',
        });
      } else {
        appToast.error('Record not found.');
        navigate('/payroll/remote-attandance');
      }
    } catch (err) {
      console.error(err);
      appToast.error('Failed to load remote attendance details.');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) {
      loadData();
    }
  }, [isEdit, loadData]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (options = { exitAfter: true, customStatus: null }) => {
    if (!form.emp_id) {
      appToast.error('Please select an employee.');
      return;
    }
    if (!form.reason_id) {
      appToast.error('Please select a reason.');
      return;
    }
    if (String(form.reason_id) === '5' && (!form.remarks || !form.remarks.trim())) {
      appToast.error('Please enter remarks for reason Other.');
      return;
    }

    setIsSaving(true);
    try {
      if (isEdit) {
        await remoteAttendanceApi.updateRecord(id, form);
        if (options.customStatus && options.customStatus !== form.status) {
          await remoteAttendanceApi.updateStatus(id, options.customStatus);
        }
        appToast.success(`Remote attendance for "${form.emp_name || 'Employee'}" updated successfully.`);
      } else {
        const created = await remoteAttendanceApi.createRecord(form);
        if (options.customStatus === 'Approved' && created?.id) {
          await remoteAttendanceApi.updateStatus(created.id, 'Approved');
        }
        appToast.success(`Remote attendance marked for "${form.emp_name || 'Employee'}" (Subject to approval).`);
      }

      if (options.exitAfter) {
        navigate('/payroll/remote-attandance');
      } else {
        setForm({
          ...DEFAULT_FORM,
          emp_id: form.emp_id,
          emp_name: form.emp_name,
        });
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || 'Failed to mark remote attendance.';
      appToast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApproveAndExit = () => handleSave({ exitAfter: true, customStatus: 'Approved' });
  const handleRejectAndExit = () => handleSave({ exitAfter: true, customStatus: 'Rejected' });

  return {
    state: {
      form,
      isEdit,
      id,
      isLoading,
      isSaving,
      reasonOptions,
    },
    actions: {
      patchForm,
      setForm,
      handleSave,
      handleApproveAndExit,
      handleRejectAndExit,
      navigate,
    },
  };
}

